// src/pages/admin/bookings/details/index.jsx
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Image, ListGroup, Modal, Spinner } from 'react-bootstrap';
import OutletHead from '../../../../components/common/outlet-head';
import { query, collection, deleteDoc, doc, getDocs, where } from 'firebase/firestore';
import { db } from './../../../../firebase';
import {
  IoArrowBackOutline,
  IoDocumentTextOutline,
  IoTrashOutline,
  IoReceiptOutline,
  IoRemove,
  IoAdd,
  IoClose
} from 'react-icons/io5';
import { CgAlignBottom } from 'react-icons/cg';
import { useAuth } from '../../../../context/AuthContext';
import { toast } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import { invoiceOutstanding, fmt, round2 } from '../../../../components/common/utils/outstanding';

const BookingDetails = () => {
  const { user } = useAuth();
  const { bid } = user.claims;

  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [item, setItem] = useState(null);
  const [invoices, setInvoices] = useState(null);

  // New-style totals
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [totalOverdueAmount, setTotalOverdueAmount] = useState(0);
  const [totalOverdueAmountPaid, setTotalOverdueAmountPaid] = useState(0);
  const [totalOutstandingAmount, setTotalOutstandingAmount] = useState(0);

  const [showDelete, setShowDelete] = useState(false);

  const handleClose = () => setShowDelete(false);
  const handleShow = () => setShowDelete(true);

  const getInvoices = async (id) => {
    try {
      const q = query(collection(db, 'Businesses', bid, 'Bookings', id, 'Invoices'));
      const querySnapshot = await getDocs(q);

      const tempData = [];
      let totalOverdue = 0;
      let totalPaidOverdue = 0;
      let paidAmount = 0;
      let totalOutstanding = 0;

      querySnapshot.forEach((snap) => {
        const data = snap.data() || {};

        const principalReceived = Number(data.principalReceived || 0);
        const taxReceived = Number(data.taxReceived || 0);
        const overdueInterest = Number(data.overdueInterest || 0);
        const overdueInterestReceived = Number(data.overdueInterestReceived || 0);
        const overdueTax = Number(data.overdueTax || 0);
        const overdueTaxReceived = Number(data.overdueTaxReceived || 0);

        // Overdue = interest + its tax
        totalOverdue += overdueInterest + overdueTax;
        totalPaidOverdue += overdueInterestReceived + overdueTaxReceived;

        // Total received = principal + tax + overdue interest + overdue tax
        paidAmount +=
          principalReceived +
          taxReceived +
          overdueInterestReceived +
          overdueTaxReceived;

        // Outstanding per invoice using shared helper
        totalOutstanding += invoiceOutstanding(data);

        tempData.push({ id: snap.id, ...data });
      });

      setInvoices(tempData);
      setTotalOverdueAmount(round2(totalOverdue));
      setTotalOverdueAmountPaid(round2(totalPaidOverdue));
      setTotalPaidAmount(round2(paidAmount));
      setTotalOutstandingAmount(round2(totalOutstanding));

      setTimeout(() => {
        setIsLoading(false);
      }, 250);
    } catch (err) {
      console.error('Error loading invoices in BookingDetails:', err);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const items = location.state;
    if (items) {
      setItem(items);
      getInvoices(items.id);
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const deleteBookingAndInvoices = async (collectionName, bookingID) => {
    try {
      const documentRef = doc(db, 'Businesses', bid, collectionName, bookingID);
      const subcollectionNames = ['Invoices'];

      for (const subcollection of subcollectionNames) {
        const subcollectionRef = collection(documentRef, subcollection);
        const subDocsSnapshot = await getDocs(subcollectionRef);
        const deleteSubDocsPromises = subDocsSnapshot.docs.map((subDoc) =>
          deleteDoc(subDoc.ref)
        );
        await Promise.all(deleteSubDocsPromises);
      }

      await deleteDoc(documentRef);
      return { success: true, message: `Deleted document ${bookingID} and its invoices.` };
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong while deleting invoices & booking!');
      return { success: false };
    }
  };

  const deleteBookedUnitByBookingID = async (propID, bookingID) => {
    try {
      const q = query(
        collection(db, 'Businesses', bid, 'Properties', propID, 'BookedUnits'),
        where('bookID', '==', bookingID)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return { success: true, message: 'No data found to delete' };
      }

      const deletePromises = querySnapshot.docs.map((snap) => deleteDoc(snap.ref));
      await Promise.all(deletePromises);
      return {
        success: true,
        message: `Deleted a booked unit for booking ID: ${bookingID}`,
      };
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong while deleting booked unit!');
      return { success: false };
    }
  };

  const deleteLedgerAndReceiptByBookingID = async (propID, bookingID) => {
    const collections = ['Ledger', 'Receipts'];
    try {
      const deletePromises = collections.map(async (collectionName) => {
        const q = query(
          collection(db, 'Businesses', bid, collectionName),
          where('bookingID', '==', bookingID)
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) return;

        const deleteOps = querySnapshot.docs.map((snap) => deleteDoc(snap.ref));
        await Promise.all(deleteOps);
      });

      await Promise.all(deletePromises);

      const bookedUnitResult = await deleteBookedUnitByBookingID(propID, bookingID);
      if (bookedUnitResult && bookedUnitResult.success) {
        return {
          success: true,
          message: 'Ledger, receipt & booked Unit deletion process completed.',
        };
      }
      return { success: false };
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong while deleting ledger & receipts!');
      return { success: false };
    }
  };

  async function handleDeleteItem() {
    if (!item) return;
    setIsDeleting(true);
    const response = await deleteLedgerAndReceiptByBookingID(
      item.projectDetails.propertyID,
      item.id
    );
    if (response && response.success) {
      deleteBookingAndInvoices('Bookings', item.id)
        .then((res) => {
          if (res && res.success) {
            toast.success('Booking Deleted, Successfully.');
            setIsDeleting(false);
            navigate(-1);
          } else {
            setIsDeleting(false);
          }
        })
        .catch((error) => {
          console.error(error);
          setIsDeleting(false);
        });
    } else {
      setIsDeleting(false);
    }
  }

  // --- Booking-level summary values (same as before) ---
  const SubTotal =
    parseFloat(item && item.basicAmount ? item.basicAmount : 0) +
    parseFloat(item && item.additionalCharges ? item.additionalCharges : 0);

  const TaxableAmount =
    parseFloat(SubTotal || 0) -
    parseFloat(item && item.discountAmount ? item.discountAmount : 0);

  const TaxAmount =
    (parseFloat(TaxableAmount || 0) * parseFloat(item && item.taxRate ? item.taxRate : 0)) /
    100;

  const TotalAmount =
    parseFloat(TaxableAmount || 0) + parseFloat(TaxAmount || 0);

  // NEW: TotalOutstanding strictly from invoices using new structure
  const TotalOutstanding = totalOutstandingAmount;

  return (
    <section className='section'>
      <OutletHead
        left={
          <div className='d-flex align-items-center'>
            <Button
              variant='light'
              size='sm'
              className='p-2 me-3 rounded-circle'
              onClick={() => navigate(-1)}
            >
              <IoArrowBackOutline size={24} />
            </Button>
            <h5 className='mb-0 fw-600'>{item && item.id}</h5>
          </div>
        }
        right={
          <div className='d-flex align-items-center justify-content-end'>
            <Button
              variant='outline-primary'
              type='button'
              className='py-2 px-4 me-3'
            >
              <IoDocumentTextOutline size={16} />
              <span className='ps-2'>Export</span>
            </Button>
            <Button
              variant='outline-danger'
              type='button'
              className='py-2 px-3 me-3'
              onClick={handleShow}
            >
              <IoTrashOutline size={16} />
              <span className='ps-2'>Delete</span>
            </Button>
            <Link to='/admin/bookings/invoices' state={{ items: item }}>
              <Button
                variant='primary'
                type='button'
                className='py-2 px-3'
              >
                <IoReceiptOutline size={16} />
                <span className='ps-2'>View Invoices</span>
              </Button>
            </Link>
          </div>
        }
      />

      {isLoading ? (
        <div className='row p-3'>
          <div className='col-md-8'>
            <Skeleton
              height={'25vh'}
              baseColor='var(--primary-light)'
              highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
            />
            <Skeleton
              height={'25vh'}
              baseColor='var(--primary-light)'
              highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              className='mt-3'
            />
            <Skeleton
              height={'33vh'}
              baseColor='var(--primary-light)'
              highlightColor='linear-gradient(to right, #f7f8ff , #A1E3F9)'
              className='mt-3'
            />
          </div>
          <div className='col-md-4'>
            <Skeleton
              height={'25vh'}
              baseColor='var(--primary-light)'
              highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
            />
            <Skeleton
              height={'25vh'}
              baseColor='var(--primary-light)'
              highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              className='mt-3'
            />
            <Skeleton
              height={'8vh'}
              baseColor='var(--primary-light)'
              highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              className='mt-3'
            />
            <Skeleton
              height={'8vh'}
              baseColor='var(--primary-light)'
              highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              className='mt-3'
            />
          </div>
        </div>
      ) : (
        <>
          {item && (
            <div className='inner-wrapper border-top'>
              <div className='row px-3'>
                {/* LEFT: Booking, Party, Property details */}
                <div className='col-md-8 pt-3'>
                  <div className='rounded border overflow-hidden mb-3'>
                    <div className='p-3 bg-primary-light'>
                      <h5 className='mb-0 text-muted fw-600'>Booking Details</h5>
                    </div>
                    <div className='d-flex align-items-center justify-content-between px-3 py-3'>
                      <h6 className='mb-0 fw-600 text-dark w-30'>Booking ID</h6>
                      <h6 className='mb-0 fw-600 text-primary text-end w-70'>
                        {item.id}
                      </h6>
                    </div>
                    <div className='d-flex align-items-center justify-content-between px-3 pb-2'>
                      <p className='mb-0 text-muted w-30'>Booking Date</p>
                      <p className='mb-0 text-end w-70'>
                        {item.bookingDate &&
                          new Date(item.bookingDate.seconds * 1000).toLocaleString(
                            'en-US',
                            {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric'
                            }
                          )}
                      </p>
                    </div>
                    {item.physicalFileNumber && (
                      <div className='d-flex align-items-center justify-content-between px-3 pb-2'>
                        <p className='mb-0 text-muted w-30'>
                          File Number <small>(Physical File No.)</small>
                        </p>
                        <p className='mb-0 text-end w-70'>
                          {item.physicalFileNumber}
                        </p>
                      </div>
                    )}
                    {item.referenceID && (
                      <div className='d-flex align-items-center justify-content-between px-3 pb-3'>
                        <p className='mb-0 text-muted w-30'>Reference ID</p>
                        <p className='mb-0 text-end w-70'>
                          {item.referenceID}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className='rounded border overflow-hidden mb-3'>
                    <div className='p-3 bg-primary-light'>
                      <h5 className='mb-0 text-muted fw-600'>Party Details</h5>
                    </div>
                    {item.partyDetails && (
                      <div className='p-3'>
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <h6 className='mb-0 fw-600 w-30'>Party Name</h6>
                          <h6 className='mb-0 fw-600 text-end w-70'>
                            {item.partyDetails.partyName}
                          </h6>
                        </div>
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <p className='mb-0 text-muted w-30'>Father/Husband</p>
                          <p className='mb-0 text-end w-70'>
                            {item.partyDetails.fatherHusband}
                          </p>
                        </div>
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <p className='mb-0 text-muted w-30'>Phone Number</p>
                          <p className='mb-0 text-end w-70'>
                            +91 {item.partyDetails.phoneNumber}
                          </p>
                        </div>
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <p className='mb-0 text-muted w-30'>Email ID</p>
                          <p className='mb-0 text-end w-70'>
                            {item.partyDetails.email}
                          </p>
                        </div>
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <p className='mb-0 text-muted w-30'>Address</p>
                          <p className='mb-0 text-end w-70'>
                            {item.partyDetails.address
                              ? item.partyDetails.address + ','
                              : ''}{' '}
                            {item.partyDetails.city
                              ? item.partyDetails.city + ','
                              : ''}{' '}
                            {item.partyDetails.pincode
                              ? item.partyDetails.pincode + ','
                              : ''}{' '}
                            {item.partyDetails.state}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className='rounded border overflow-hidden mb-3'>
                    <div className='p-3 bg-primary-light'>
                      <h5 className='mb-0 text-muted fw-600'>Property Details</h5>
                    </div>
                    <div className='row p-3'>
                      <div className='col-8 pb-3'>
                        <div className='d-flex align-items-start'>
                          <div className='bg-primary-light rounded p-2'>
                            <CgAlignBottom size={34} color='#D4D4D4' />
                          </div>
                          <div className='ms-3'>
                            <h5 className='mb-0'>
                              <small>
                                {item.projectDetails &&
                                  item.projectDetails.propName}
                              </small>
                            </h5>
                            <p className='mb-0 text-secondary'>
                              <small>
                                {item.projectDetails &&
                                  item.projectDetails.propLocality}
                                ,{' '}
                                {item.projectDetails &&
                                  item.projectDetails.propCity}
                                ,{' '}
                                {item.projectDetails &&
                                  item.projectDetails.propPincode}
                                ,{' '}
                                {item.projectDetails &&
                                  item.projectDetails.propState}
                              </small>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className='col-4 pb-3'>
                        <div>
                          <p className='mb-0 text-end'>
                            <small>
                              Floor -{' '}
                              <span className='fw-600'>
                                {item.projectDetails &&
                                  item.projectDetails.bookedUnit &&
                                  item.projectDetails.bookedUnit.unitFloor}
                              </span>
                            </small>
                          </p>
                          <p className='mb-0 text-end'>
                            <small>
                              Unit No. -{' '}
                              <span className='fw-600'>
                                {item.projectDetails &&
                                  item.projectDetails.bookedUnit &&
                                  item.projectDetails.bookedUnit.unitFloor}
                                0
                                {item.projectDetails &&
                                  item.projectDetails.bookedUnit &&
                                  item.projectDetails.bookedUnit.unitNumber}
                              </span>
                            </small>
                          </p>
                        </div>
                      </div>

                      {item.projectDetails && (
                        <div className='my-2'>
                          <div className='row'>
                            <div className='col-4 bg-light text-muted px-3 py-2'>
                              <small>Unit Name</small>
                            </div>
                            <div className='col-2 bg-light text-muted text-center px-3 py-2'>
                              <small>Area</small>
                            </div>
                            <div className='col-3 bg-light text-muted text-center px-3 py-2'>
                              <small>Basic Price</small>
                            </div>
                            <div className='col-3 bg-light text-muted text-end px-3 py-2'>
                              <small>Amount</small>
                            </div>
                          </div>
                          <div className='row mb-2'>
                            <div className='col-4 px-3 py-2'>
                              {item.projectDetails.bookedUnit &&
                                item.projectDetails.bookedUnit.unitName}
                            </div>
                            <div className='col-2 text-center px-3 py-2'>
                              {item.projectDetails.bookedUnit &&
                                item.projectDetails.bookedUnit.unitArea}{' '}
                              {item.projectDetails.propUnitMeasure}
                            </div>
                            <div className='col-3 text-center px-3 py-2'>
                              ₹
                              {item.projectDetails.bookedUnit &&
                                item.projectDetails.bookedUnit.unitbasicPrice}
                              /
                              {item.projectDetails.propUnitMeasure}
                            </div>
                            <div className='col-3 text-end px-3 py-2'>
                              {fmt(
                                (parseFloat(
                                  item.projectDetails.bookedUnit &&
                                  item.projectDetails.bookedUnit
                                    .unitbasicPrice
                                ) || 0) *
                                (parseFloat(
                                  item.projectDetails.bookedUnit &&
                                  item.projectDetails.bookedUnit.unitArea
                                ) || 0)
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {item.projectDetails &&
                        item.projectDetails.bookedUnit &&
                        item.projectDetails.bookedUnit.unitAdditionalCharges &&
                        item.projectDetails.bookedUnit.unitAdditionalCharges
                          .length > 0 && (
                          <div className='my-2'>
                            <div className='row'>
                              <div className='col-6 bg-light text-muted px-3 py-2'>
                                <small>Additional Charges</small>
                              </div>
                              <div className='col-3 bg-light text-muted text-center px-3 py-2'>
                                <small>Rate</small>
                              </div>
                              <div className='col-3 bg-light text-muted text-end px-3 py-2'>
                                Amount
                              </div>
                            </div>
                            {item.projectDetails.bookedUnit.unitAdditionalCharges.map(
                              (charge, i) => {
                                const rate = parseFloat(charge.amount) || 0;
                                const area =
                                  parseFloat(
                                    item.projectDetails.bookedUnit.unitArea
                                  ) || 0;
                                const amount =
                                  charge.type === 'per'
                                    ? rate * area
                                    : rate;
                                return (
                                  <div key={i} className='row'>
                                    <div className='col-6 px-3 py-2'>
                                      {charge.name}
                                    </div>
                                    <div className='col-3 text-center px-3 py-2'>
                                      {fmt(rate)}
                                      {charge.type === 'per'
                                        ? '/' +
                                        item.projectDetails.propUnitMeasure
                                        : null}
                                    </div>
                                    <div className='col-3 text-end px-3 py-2'>
                                      {fmt(amount)}
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {/* RIGHT: Summary & Payment Plans */}
                <div className='col-md-4 ps-md-0'>
                  <div className='sticky-top pt-3'>
                    {/* Summary */}
                    <div className='rounded border overflow-hidden mb-3'>
                      <div className='p-3 bg-primary-light border-bottom'>
                        <h5 className='mb-0 text-muted fw-600'>Summary</h5>
                      </div>
                      <div className='h-100 d-flex flex-column justify-content-between p-3'>
                        <div className='mb-3'>
                          <div className='d-flex align-items-center justify-content-between mb-1'>
                            <p className='mb-0 text-muted'>Sub Total</p>
                            <p className='mb-0 text-muted'>{fmt(SubTotal)}</p>
                          </div>
                          <div className='d-flex align-items-center justify-content-between mb-1'>
                            <p className='mb-0 text-muted'>
                              Discount{' '}
                              <span>
                                ({item && item.discountPercent && item.discountPercent}
                                %)
                              </span>
                            </p>
                            <p className='mb-0 text-muted'>
                              <IoRemove /> {fmt(item?.discountAmount || 0)}
                            </p>
                          </div>
                          <div className='d-flex align-items-center justify-content-between mb-1'>
                            <p className='mb-0 text-muted'>Tax Amount</p>
                            <p className='mb-0 text-muted'>
                              <IoAdd /> {fmt(TaxAmount)}
                            </p>
                          </div>
                        </div>
                        <div className='border-top pt-3'>
                          <div className='d-flex align-items-center justify-content-between'>
                            <h5 className='mb-0 fw-600'>Total Amount</h5>
                            <h5 className='mb-0 fw-600'>{fmt(TotalAmount)}</h5>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Payment Plans */}
                    <div className='rounded border overflow-hidden mb-3'>
                      <div className='p-3 bg-primary-light border-bottom'>
                        <h5 className='mb-0 text-muted fw-600'>Payment Plans</h5>
                      </div>
                      <ListGroup variant='flush' className='paid-xl-icon'>
                        {invoices &&
                          invoices.map((invItem, index) => {
                            const principal = Number(invItem.principal || 0);
                            const tax = Number(invItem.tax || 0);
                            const overdueInterest =
                              Number(invItem.overdueInterest || 0);
                            const overdueTax = Number(invItem.overdueTax || 0);

                            const instalmentAmount = principal;
                            const instalmentTaxAmount = tax;

                            return (
                              <ListGroup.Item variant='light' key={invItem.id || index}>
                                <div
                                  className={
                                    invItem.isPaid
                                      ? 'd-flex align-items-center justify-content-between py-1 text-secondary'
                                      : 'd-flex align-items-start justify-content-between py-1'
                                  }
                                >
                                  <div className='d-flex'>
                                    <div className='me-1'>
                                      <span>{index + 1}.</span>
                                    </div>
                                    <div>
                                      <div>{invItem.name}</div>
                                      <div className='small text-muted'>
                                        {invItem.description}
                                      </div>
                                    </div>
                                  </div>
                                  {
                                    invItem.isPaid ?
                                      <div className="text-decoration-none text-end small alert alert-success text-success border-0 px-2 py-0 mb-0">
                                        <small>Paid</small>
                                      </div>
                                      :
                                      <div
                                        className={
                                          invItem.isPaid
                                            ? 'text-decoration-line-through text-end small'
                                            : 'text-decoration-none text-end small'
                                        }
                                      >
                                        {fmt(instalmentAmount)} + {fmt(instalmentTaxAmount)}
                                      </div>
                                  }
                                </div>
                                {overdueInterest !== 0 || overdueTax !== 0 ? (
                                  <div className='d-flex align-items-center justify-content-between text-danger text-end small'>
                                    <div className='ms-3'>
                                      Interest ({item.overdueInterestRate}%){' '}
                                      {typeof invItem.overdueDays === 'number' &&
                                        `on ${invItem.overdueDays} days`}
                                    </div>
                                    <div>
                                      {fmt(overdueInterest)} + {fmt(overdueTax)}
                                    </div>
                                  </div>
                                ) : null}
                              </ListGroup.Item>
                            );
                          })}
                      </ListGroup>
                    </div>

                    {/* Totals using new structure */}
                    <div className='bg-primary-light rounded border overflow-hidden mb-3'>
                      <div className='d-flex align-items-center justify-content-between text-danger p-3'>
                        <h5 className='mb-0 fw-600'>Total Overdue</h5>
                        <h5 className='mb-0 fw-600'>
                          {fmt(totalOverdueAmount)}
                        </h5>
                      </div>
                    </div>
                    <div className='bg-primary-light rounded border overflow-hidden mb-3'>
                      <div className='d-flex align-items-center justify-content-between text-danger p-3'>
                        <h5 className='mb-0 fw-600'>Overdue Received</h5>
                        <h5 className='mb-0 fw-600'>
                          {fmt(totalOverdueAmountPaid)}
                        </h5>
                      </div>
                    </div>
                    <div className='bg-primary-light rounded border overflow-hidden mb-3'>
                      <div className='d-flex align-items-center justify-content-between text-success p-3'>
                        <h5 className='mb-0 fw-600'>Total Received</h5>
                        <h5 className='mb-0 fw-600'>
                          {fmt(totalPaidAmount)}
                        </h5>
                      </div>
                    </div>
                    {
                      TotalOutstanding !== 0 ?
                        <div className='bg-primary-light rounded border overflow-hidden mb-3'>
                          <div className='d-flex align-items-center justify-content-between text-danger p-3'>
                            <h5 className='mb-0 fw-600'>Total Outstanding</h5>
                            <h5 className='mb-0 fw-600'>
                              {fmt(TotalOutstanding)}
                            </h5>
                          </div>
                        </div>
                        :
                        null
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )
      }

      {/* Delete modal */}
      <Modal
        show={showDelete}
        onHide={handleClose}
        backdrop='static'
        keyboard={false}
        contentClassName='border-0 shadow-sm'
        centered
      >
        <div className='bg-light overflow-hidden rounded'>
          <div className='d-flex align-items-center justify-content-between'>
            <h5 className='mb-0 p-3'>Delete</h5>
            <Button
              variant='white'
              className='rounded border-0 px-3'
              onClick={handleClose}
            >
              <IoClose size={20} />
            </Button>
          </div>
        </div>
        <div className='border-top px-4 pt-4'>
          <h6 className='mb-0'>
            Are you sure, you want to delete this booking?
          </h6>
          <div className='py-3'>
            <p className='mb-0 text-muted small'>
              <span className='fw-600 text-decoration-underline'>Warning</span> - Deleting
              this booking will permanently remove it and delete all associated data
              given below:
            </p>
            <ul className='text-muted pt-3 ps-4'>
              <li style={{ listStyleType: 'circle' }} className='small'>
                Invoices
              </li>
              <li style={{ listStyleType: 'circle' }} className='small mt-1'>
                Receipts
              </li>
              <li style={{ listStyleType: 'circle' }} className='small mt-1'>
                Ledger Entry
              </li>
            </ul>
          </div>
        </div>
        <div className='d-flex align-items-center justify-content-end px-3 pb-3'>
          <Button
            disabled={isDeleting}
            variant='light'
            onClick={handleClose}
            className='border px-5 py-2 me-3'
          >
            Cancel
          </Button>
          <Button
            disabled={isDeleting}
            variant='danger'
            className='px-5 py-2 btn-sm-width'
            onClick={handleDeleteItem}
          >
            {isDeleting ? (
              <div>
                <Spinner size='sm' />
              </div>
            ) : (
              <span>Delete</span>
            )}
          </Button>
        </div>
      </Modal>
    </section >
  );
};

export default BookingDetails;